import React, { useCallback, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, Image as ImageIcon, X } from "lucide-react";
import { useLang } from "@/contexts/AppContexts";

const MAX_BYTES = 15 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

const Dropzone = ({ onFile, file, onClear, disabled }) => {
  const { t } = useLang();
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const validateAndSet = useCallback(
    (f) => {
      if (!f) return;
      if (!ALLOWED.includes(f.type)) {
        setError(t("errors.bad_format"));
        return;
      }
      if (f.size > MAX_BYTES) {
        setError(t("errors.file_too_large"));
        return;
      }
      setError(null);
      onFile && onFile(f);
    },
    [onFile, t]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    validateAndSet(f);
  };

  const onSelect = (e) => {
    const f = e.target.files?.[0];
    validateAndSet(f);
  };

  const previewUrl = file ? URL.createObjectURL(file) : null;

  return (
    <div className="space-y-3">
      <Card
        className={`relative overflow-hidden border-2 border-dashed transition-colors ${
          drag
            ? "border-primary/60 bg-primary/5"
            : error
            ? "border-destructive/60 bg-destructive/5"
            : "border-border bg-card hover:border-primary/40 hover:bg-muted/30"
        } ${disabled ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        data-testid="upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onSelect}
          className="hidden"
          data-testid="upload-file-input"
          disabled={disabled}
        />

        {file && previewUrl ? (
          <div className="flex items-center gap-4 p-4">
            <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border bg-muted">
              <img
                src={previewUrl}
                alt={file.name}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="font-mono-plex text-xs text-muted-foreground truncate"
                title={file.name}
              >
                {file.name}
              </p>
              <p className="text-sm font-medium">
                {(file.size / 1024).toFixed(1)} KB · {file.type.replace("image/", "").toUpperCase()}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("upload.dropzone_hint")}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onClear && onClear();
              }}
              aria-label={t("upload.reset")}
              data-testid="upload-clear-button"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary text-foreground/80">
              <Upload className="h-6 w-6" />
            </div>
            <div>
              <p className="font-display text-base font-semibold">
                {t("upload.dropzone_title")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground font-mono-plex">
                {t("upload.dropzone_hint")}
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
              data-testid="upload-pick-button"
            >
              <ImageIcon className="h-4 w-4 mr-1.5" />
              {t("upload.pick_file")}
            </Button>
          </div>
        )}
      </Card>
      {error && (
        <p className="text-xs text-destructive font-medium" data-testid="upload-error">
          {error}
        </p>
      )}
      <p className="text-[11px] text-muted-foreground">{t("upload.privacy_note")}</p>
    </div>
  );
};

export { Dropzone };
export default Dropzone;
